/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Import API client
import captureClient, { isSessionError, getErrorMessage } from '../api/captureClient';
// Import extracted components
import { Capturing, Done, Instructions, Review, ResumeGate, SavedExit, Submitting, Welcome } from '../components/capture';
import Modal from '../components/Modal';
// Import extracted utilities
import { normalizeCapturedEntry, normalizeMediaArray, requestLocation } from '../utils/captureUtils';
import { captureProvenanceMetadata } from '../utils/provenanceService';
import { deleteSessionToken } from '../utils/cookieService';
import { getNoteIdentity } from '../utils/noteIdentityService';
import { NOTES_SAVE_EXIT_ENABLED } from '../constants/featureFlags';
import OtpVerification from '../components/capture/OtpVerification';
import offlineDb from '../utils/offlineDb';
import captureSyncQueue from '../utils/captureSyncQueue';
import assetCache from '../utils/assetCache';
import useOnlineStatus from '../hooks/useOnlineStatus';
import useExpiryReminders from '../hooks/useExpiryReminders';
import OfflineBanner from '../components/OfflineBanner';
import AssetSyncOverlay from '../components/AssetSyncOverlay';
import DataSyncOverlay from '../components/DataSyncOverlay';
import SyncStatusToast from '../components/SyncStatusToast';
import { getMediaCategory } from '../constants/mimeMap';
import { STATIC_CAPTURE_ICON_URLS, PDF_WORKER_SRC } from '../constants/staticCaptureAssets';
import NoteIdentityGate from '../components/capture/NoteIdentityGate';
import NotesModal from '../components/capture/NotesModal';

const HTTP_STATUS_NOT_FOUND = 404;
const HTTP_STATUS_GONE = 410;
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;
// Phases that pass through the note-identity gate before being shown.
const NOTE_GATED_PHASES = new Set(['capturing', 'review']);
// With the Notes/Save & Exit feature off there is no name/email gate at all,
// so every device counts as already identified (see constants/featureFlags).
const hasCaptureIdentity = (token) => !NOTES_SAVE_EXIT_ENABLED || Boolean(getNoteIdentity(token));
// Phases reachable before this device has claimed its own session — Notes
// needs a conflict re-check there since its own fetch skips session validation.
// Scoped to just these two phases so the (heavier) GET :token re-check isn't
// fired on every Notes click, only where the race is actually possible.
const NOTES_GUARD_PHASES = new Set(['instructions', 'resume-gate']);
// Phases where there's nothing actionable to "hurry up and finish" — same
// exclusion already used for OfflineBanner/SyncStatusToast below, plus
// saved-exit-done (reached only after handleConfirmSaveAndExit has already
// released the session server-side, so the link expiring is moot there).
// saved-exit itself is deliberately NOT excluded — the session isn't
// released until the technician taps "Save" on that screen, so they still
// stand to lose it if the link expires while they're deciding.
const EXPIRY_REMINDER_EXCLUDED_PHASES = new Set(['welcome', 'done', 'otp-verification', 'saved-exit-done']);

const resumeGateStorageKey = (token) => `send2me_resume_gate_pending_${token}`;
const getStoredResumeTarget = (token) => {
  try {
    const raw = sessionStorage.getItem(resumeGateStorageKey(token));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setStoredResumeTarget = (token, target) => {
  try {
    sessionStorage.setItem(resumeGateStorageKey(token), JSON.stringify(target));
  } catch {
    // Best-effort only — worst case a refresh on ResumeGate falls through
    // to the normal resume logic instead of staying on the gate.
  }
};

const clearStoredResumeTarget = (token) => {
  try {
    sessionStorage.removeItem(resumeGateStorageKey(token));
  } catch {
    // No-op — see getStoredResumeTarget.
  }
};

// Branding/favicon/photo-preview images — small, so the default 8MB cache
// ceiling is fine. Reference media (image/video) uses a bigger ceiling below.
const collectImageAssetUrls = (data, completedMediaMap) => {
  const urls = [data?.workflow?.branding?.logoUrl, data?.tenant?.faviconUrl, PDF_WORKER_SRC, ...STATIC_CAPTURE_ICON_URLS];
  Object.values(completedMediaMap || {}).flat().forEach((media) => {
    if (!media?.url) return;
    const category = getMediaCategory(media.mimeType, media.fileName);
    if (category !== 'video' && category !== 'audio') {
      urls.push(media.url);
    }
  });
  return urls;
};

// Customer-captured video/audio — split out from collectImageAssetUrls above
// so it can be prefetched under the bigger REFERENCE_VIDEO_ASSET_MAX_BYTES
// ceiling instead of the small default used for photo/identifier thumbnails
// (same crash-risk reasoning as reference video below).
const collectCompletedLargeMediaAssetUrls = (completedMediaMap) =>
  Object.values(completedMediaMap || {}).flat()
    .filter((media) => {
      if (!media?.url) return false;
      const category = getMediaCategory(media.mimeType, media.fileName);
      return category === 'video' || category === 'audio';
    })
    .map((media) => media.url);

// Reference media — the admin-configured help image/video behind the "?"
// icon in MediaUploader's FullScreenViewer. Cached under a bigger ceiling
// than default since uploads allow much larger files than icons/thumbnails.
const collectReferenceImageAssetUrls = (data) =>
  (data?.items || [])
    .map((item) => item.referenceMediaUrl)
    .filter((url) => url && getMediaCategory(null, url) !== 'video');

const collectReferenceVideoAssetUrls = (data) =>
  (data?.items || [])
    .map((item) => item.referenceMediaUrl)
    .filter((url) => url && getMediaCategory(null, url) === 'video');

// Matches StepReferenceMediaUpload.jsx's IMAGE_MAX_SIZE.
const REFERENCE_IMAGE_ASSET_MAX_BYTES = 200 * 1024 * 1024;
// Kept below the video upload ceiling — fetch().blob() holds the whole
// file in memory, so a huge video is a mobile-tab-crash risk.
const REFERENCE_VIDEO_ASSET_MAX_BYTES = 75 * 1024 * 1024;
// The admin-configured global instruction (image/video/PDF behind the "?"
// icon in CaptureHeader) — same crash-risk reasoning as reference video.
const GLOBAL_INSTRUCTION_ASSET_MAX_BYTES = 75 * 1024 * 1024;

// Human-readable label for what kind of queued change a sync error toast is
// about — paired with getErrorMessage(error) for the "why" part.
const SYNC_MUTATION_LABELS = {
  upload: 'A photo or video',
  deleteItem: 'Removing a step',
  deletePhoto: 'Removing a photo',
  skip: 'Skipping a step',
  beginCapture: 'Starting capture',
  complete: 'Finishing your submission',
  addNote: 'Saving a note',
  updateNote: 'Updating a note',
  deleteNote: 'Deleting a note',
  saveAndExit: 'Saving your progress',
  start: 'Marking this link as opened',
  provenance: 'Recording device details',
};

// Same pattern as the resume-gate target above, but for Save & Exit: that
// screen is reached client-side only (handleSaveAndExit just flips `phase`,
// nothing is persisted server-side until the user taps Save), so a refresh
// would otherwise fall through to the normal server-driven resume logic and
// land back on Review/Capturing instead of staying on Save & Exit.
const savedExitStorageKey = (token) => `send2me_saved_exit_pending_${token}`;
const getStoredSavedExitPending = (token) => {
  try {
    return sessionStorage.getItem(savedExitStorageKey(token)) === 'true';
  } catch {
    return false;
  }
};

const setStoredSavedExitPending = (token) => {
  try {
    sessionStorage.setItem(savedExitStorageKey(token), 'true');
  } catch {
    // Best-effort only — see getStoredResumeTarget.
  }
};

const clearStoredSavedExitPending = (token) => {
  try {
    sessionStorage.removeItem(savedExitStorageKey(token));
  } catch {
    // No-op — see getStoredResumeTarget.
  }
};

// sessionCapturedItemIds (below) drives which items SavedExit lets this
// device retake/delete — it otherwise lives only in React state, so without
// this it silently resets to empty on refresh and a Save & Exit restored via
// the flag above would render read-only instead of editable.
const sessionCapturedItemsStorageKey = (token) => `send2me_session_captured_items_${token}`;
const getStoredSessionCapturedItemIds = (token) => {
  try {
    const raw = sessionStorage.getItem(sessionCapturedItemsStorageKey(token));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const setStoredSessionCapturedItemIds = (token, itemIds) => {
  try {
    sessionStorage.setItem(sessionCapturedItemsStorageKey(token), JSON.stringify([...itemIds]));
  } catch {
    // Best-effort only — see getStoredResumeTarget.
  }
};

const Capture = () => {
  const { token: rawToken } = useParams();
  // A shared/QR-code link can carry a stray control character (an embedded
  // newline/tab from copy-paste or QR encoding) that Chromium silently
  // strips while parsing a request URL but WebKit's stricter parser rejects
  // outright as "Bad URL" — sanitize once here since every downstream use
  // (API requests, IndexedDB keys) derives from this single value.
  const token = rawToken?.trim().replace(/[\x00-\x1F\x7F]/g, '');
  const navigate = useNavigate();
  const [captureData, setCaptureData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phases: welcome → instructions → capturing → review → submitting → done
  const [phase, setPhase] = useState('welcome');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // Items already completed on the server (from a previous session)
  const [serverCompleted, setServerCompleted] = useState({});
  // Session recovery state
  const [sessionRecovered, setSessionRecovered] = useState(false);
  const [completedItemsMedia, setCompletedItemsMedia] = useState({});
  // Files captured in this session, held locally before final submit
  // { [assignmentItemId]: { file, previewUrl, identifierText, item } }
  const [capturedFiles, setCapturedFiles] = useState({});
  // Upload progress per item during submit phase
  const submitProgress = { current: 0, total: 0, itemIndex: 0 };
  const [submitError, setSubmitError] = useState('');
  // Set specifically when handleSubmit's pendingSyncCount guard blocks a
  // submit attempt — kept separate from submitError (a generic in-page
  // banner) so Review.jsx can show this one as a toast right above the
  // submit button instead, without touching how other submitError cases
  // (missing required items, a real server rejection) are displayed.
  const [syncBlockedNotice, setSyncBlockedNotice] = useState(false);
  const [location, setLocation] = useState(null);
  const [pendingPhase, setPendingPhase] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notesStepItemId, setNotesStepItemId] = useState(null);
  const [notesOpenMode, setNotesOpenMode] = useState('list');
  const [notes, setNotes] = useState([]);
  const startedRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  // Track which item we came from when retaking from review or adding another photo
  const retakeItemIdRef = useRef(null);
  const addPhotoItemIdRef = useRef(null);
  const addMorePhotoItemIdRef = useRef(null);
  // Tracks whether the current retake came from the multi-photo review screen
  const retakeFromMultiPhotoReviewRef = useRef(false);
  // Holds the old mediaFileId to delete after a successful retake upload
  const retakeMediaFileIdRef = useRef(null);
  const retakePhotoIndexRef = useRef(null);
  const retakeReturnPhaseRef = useRef('review');
  const [sessionCapturedItemIds, setSessionCapturedItemIds] = useState(() => new Set(getStoredSessionCapturedItemIds(token)));
  const pendingResumeRef = useRef(null);
  // State-driven retake signal so MediaUploader re-renders with retakeMode=true
  const [retakeItemId, setRetakeItemId] = useState(null);
  const [replaceUploadContext, setReplaceUploadContext] = useState(null);
  // Multi-photo review: assignmentItemId of the step currently in multi-photo review, or null
  const [multiPhotoReviewItemId, setMultiPhotoReviewItemId] = useState(null);
  // Session conflict state
  const [showSessionConflictModal, setShowSessionConflictModal] = useState(false);
  const [sessionConflictBranding, setSessionConflictBranding] = useState(null);
  const [sessionConflictMessage, setSessionConflictMessage] = useState(null);
  // Offline resilience state
  const isOnline = useOnlineStatus();
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  // True while the initial landing-page asset prefetch (branding, reference
  // media, global instructions) is still in flight. Blocks the UI so the
  // customer can't navigate to a step whose media hasn't been cached yet.
  const [isSyncingAssets, setIsSyncingAssets] = useState(false);
  // True while the offline queue is actively replaying (uploads/mutations/
  // notes) after reconnecting — blocks the UI so the customer can't
  // navigate or trigger another action mid-sync.
  const [isSyncingData, setIsSyncingData] = useState(false);
  // One-off result of the most recent queue flush — separate from
  // pendingSyncCount (a standing tally) since this is a transient toast.
  const [syncStatus, setSyncStatus] = useState(null); // { status: 'success' | 'error', message } | null
  // True once useExpiryReminders' onExpired fires (see handleExpiryReached
  // below) — drives an undismissable "sync your data" alert distinct from
  // the dismissable expiry-reminder toasts above. Cleared once back online
  // (see the isOnline effect below): either the link really is expired, in
  // which case the existing reconcile-then-navigate-to-/error flow takes
  // over and unmounts this page anyway, or the device clock was just
  // running ahead and the link turns out to still be valid, in which case
  // the alert would be wrong to keep showing.
  const [showExpiredOfflineAlert, setShowExpiredOfflineAlert] = useState(false);
  const prevPendingSyncCountRef = useRef(0);
  const syncHadErrorRef = useRef(false);
  const pendingSubmitRef = useRef(false);
  // Defaults false so the brief pre-load window (before captureData arrives)
  // doesn't touch IndexedDB for an assignment that turns out to have offline
  // support disabled. fetchCaptureData recomputes the real value fresh from
  // the response for cache warmup, independent of this component-level default.
  const offlineSupportEnabled = captureData?.assignment?.offlineSupportEnabled ?? false;
  // Caching (not uploads/mutations) is skipped entirely when disabled — see assetCache.js.
  const cacheToken = offlineSupportEnabled ? token : null;

  // Corrects the expiry countdown below for a wrong device clock — see
  // useExpiryReminders. Falls back to 0 (trust the device clock) if the
  // server hasn't started returning serverTime yet.
  const clockOffsetMs = useMemo(() => {
    const serverTime = captureData?.serverTime;
    if (!serverTime) return 0;
    const parsed = new Date(serverTime).getTime();
    return Number.isNaN(parsed) ? 0 : parsed - Date.now();
  }, [captureData?.serverTime]);
  // Set (while still offline) the moment the countdown hits zero; consumed
  // by the "reconnect and reconcile" effect further down once back online.
  // Also flips on the undismissable "sync your data" alert below — reusing
  // useExpiryReminders' existing background poll (every 15s, plus on tab
  // visibility change) is the same mechanism that already powers the
  // 30/15/5/1-minute reminder toasts, so it fires reliably even if the
  // device sat idle with no further capture activity.
  const expiryReachedRef = useRef(false);
  const handleExpiryReached = useCallback(() => {
    expiryReachedRef.current = true;
    setShowExpiredOfflineAlert(true);
  }, []);
  // Reminders only ever fire offline, on an offline-capable assignment, and
  // while there's something actionable to finish — see the confirmed
  // trigger scope in hazy-cooking-planet.md. An online technician can just
  // retry a failing request instead, so there's nothing useful this adds there.
  const expiryRemindersEnabled = Boolean(captureData) && offlineSupportEnabled && !isOnline && !EXPIRY_REMINDER_EXCLUDED_PHASES.has(phase);
  const { activeReminder: expiryReminder, dismissReminder: dismissExpiryReminder, resetReminders: resetExpiryReminders } = useExpiryReminders({
    token,
    expiresAt: captureData?.assignment?.expiresAt,
    createdAt: captureData?.assignment?.createdAt,
    clockOffsetMs,
    enabled: expiryRemindersEnabled,
    onExpired: handleExpiryReached,
  });

  // Reconnecting either confirms the link is genuinely dead (the existing
  // reconcile-then-navigate-to-/error flow below takes over and unmounts this
  // page) or, if the device clock was just running ahead, shows the link is
  // still valid — either way the alert shouldn't keep showing once online.
  useEffect(() => {
    if (isOnline) setShowExpiredOfflineAlert(false);
  }, [isOnline]);

  const goToPhase = useCallback((nextPhase) => {
    if (!hasCaptureIdentity(token)) {
      setPendingPhase(nextPhase);
      return;
    }
    setPhase(nextPhase);
  }, [token]);

  // Only 'capturing'/'review' route through the note-identity gate —
  // 'welcome'/'otp-verification'/'resume-gate' bypass it. Shared by the
  // initial-load resume logic and handleResumeContinue below.
  const commitPhase = useCallback((targetPhase) => {
    if (NOTE_GATED_PHASES.has(targetPhase)) {
      goToPhase(targetPhase);
    } else {
      setPhase(targetPhase);
    }
  }, [goToPhase]);

  // Read-only notes preview for NotesSummary — best-effort; the NotesModal
  // has its own error state for the full-featured view. Routed through
  // captureSyncQueue.fetchNotes (same as NotesModal) so offline/network
  // failures fall back to the local mirror instead of leaving the summary
  // silently stale relative to notes queued via NotesModal.
  const fetchNotesSummary = useCallback(async () => {
    try {
      const identity = getNoteIdentity(token);
      const result = await captureSyncQueue.fetchNotes({
        token,
        name: identity?.name,
        email: identity?.email,
        offlineSupportEnabled,
      });
      setNotes(result || []);
    } catch {
      // Ignore — the summary is supplementary.
    }
  }, [token, offlineSupportEnabled]);

  useEffect(() => {
    if (NOTES_SAVE_EXIT_ENABLED) fetchNotesSummary();
  }, [fetchNotesSummary]);

  const openNotes = async (assignmentItemId = null, mode = 'list') => {
    if (NOTES_GUARD_PHASES.has(phase)) {
      try {
        await captureClient.get(`/${token}`);
      } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.sessionConflict) {
          setSessionConflictBranding(err.response.data.branding || null);
          setShowSessionConflictModal(true);
          return;
        }
        // Any other error (network hiccup, etc.) shouldn't block Notes.
      }
    }
    setNotesStepItemId(assignmentItemId);
    setNotesOpenMode(mode);
    setIsNotesModalOpen(true);
  };

  // Undefined while the feature is off — every Notes button/summary renders
  // only when its handler is passed.
  const handleOpenNotes = NOTES_SAVE_EXIT_ENABLED ? () => openNotes() : undefined;
  const handleAddNote = NOTES_SAVE_EXIT_ENABLED ? () => openNotes(null, 'add') : undefined;

  const fetchCaptureData = useCallback(async (skipNavigation = false, forceNew = false) => {
    // Guard: if a fetch is already in-flight and this isn't a forced new-session call,
    // drop the duplicate (caused by React Strict Mode double-invoking useEffect).
    if (fetchInProgressRef.current && !forceNew) return;
    fetchInProgressRef.current = true;
    setLoading(true);

    try {

      // Call capture API - session token will be auto-added by interceptor
      const response = await captureClient.get(`/${token}`);

      setCaptureData(response.data);
      // Freshly computed — captureData state above hasn't re-rendered yet,
      // so the component-level offlineSupportEnabled would still be stale here.
      const offlineEnabledForThisFetch = response.data?.assignment?.offlineSupportEnabled ?? true;

      // Check if assignment is already completed
      if (response.data.assignment.status === 'completed') {
        // Still need to populate serverCompleted for the Done component
        const initialItems = response.data.items;
        const initialCompletedItems = {};
        const initialCompletedMedia = {};
        let completedCount = 0;

        initialItems.forEach(item => {
          if (item.status === 'completed') {
            initialCompletedItems[item.assignmentItemId] = true;
            completedCount++;

            // Store completed media for display
            const normalizedMedia = normalizeMediaArray(item.media);
            if (normalizedMedia.length > 0) {
              initialCompletedMedia[item.assignmentItemId] = normalizedMedia.map((mediaEntry) => ({
                ...mediaEntry,
                url: mediaEntry.url,
                fileName: mediaEntry.fileName,
                mimeType: mediaEntry.mimeType,
                fileSize: mediaEntry.fileSize,
                type: item.type,
                completedAt: item.completedAt,
                duration: mediaEntry.duration,
                identifierText: item.customerIdentifierText ?? item.identifierText ?? mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
                customerIdentifierText: item.customerIdentifierText ?? item.identifierText ?? mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
                ocrIdentifierText: item.identifierText ?? mediaEntry.identifierText,
              }));
            }
          }
        });

        setServerCompleted(initialCompletedItems);
        setCompletedItemsMedia(initialCompletedMedia);

        // Set the completed count in captureData for Done component
        setCaptureData(prev => ({
          ...prev,
          completedItemsCount: completedCount
        }));

        if (!skipNavigation) {
          setPhase('done');
        }
        offlineDb.deleteDatabase(token).catch(() => {});
        return initialCompletedItems;
      }

      // Session Recovery Logic
      const fetchedItems = response.data.items;
      const completedItems = {};
      const completedMedia = {};

      fetchedItems.forEach(item => {
        if (item.status === 'completed') {
          completedItems[item.assignmentItemId] = true;

          // Store completed media for display
          const normalizedMedia = normalizeMediaArray(item.media);
          if (normalizedMedia.length > 0) {
            completedMedia[item.assignmentItemId] = normalizedMedia.map((mediaEntry) => ({
              ...mediaEntry,
              url: mediaEntry.url,
              fileName: mediaEntry.fileName,
              mimeType: mediaEntry.mimeType,
              fileSize: mediaEntry.fileSize,
              type: item.type,
              completedAt: item.completedAt,
              duration: mediaEntry.duration,
              identifierText: item.customerIdentifierText ?? item.identifierText ?? mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
              customerIdentifierText: item.customerIdentifierText ?? item.identifierText ?? mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
              ocrIdentifierText: item.identifierText ?? mediaEntry.identifierText,
            }));
          }
        }
      });

      setServerCompleted(completedItems);
      setCompletedItemsMedia(completedMedia);

      // Warm the offline cache in the background. Skipped entirely when
      // offline support is off, so nothing ever touches IndexedDB.
      if (offlineEnabledForThisFetch) {
        const prefetchSettled = Promise.allSettled([
          assetCache.prefetchAssets(token, collectImageAssetUrls(response.data, completedMedia)),
          assetCache.prefetchAssets(token, collectCompletedLargeMediaAssetUrls(completedMedia), { maxBytes: REFERENCE_VIDEO_ASSET_MAX_BYTES }),
          assetCache.prefetchAssets(token, collectReferenceImageAssetUrls(response.data), { maxBytes: REFERENCE_IMAGE_ASSET_MAX_BYTES }),
          assetCache.prefetchAssets(token, collectReferenceVideoAssetUrls(response.data), { maxBytes: REFERENCE_VIDEO_ASSET_MAX_BYTES }),
          assetCache.prefetchAssets(token, [response.data?.workflow?.globalInstructionUrl], { maxBytes: GLOBAL_INSTRUCTION_ASSET_MAX_BYTES }),
        ]);
        // Only block the UI for the initial landing fetch — background
        // refreshes (retake/submit/etc.) shouldn't re-show the sync overlay.
        if (!skipNavigation) {
          setIsSyncingAssets(true);
          prefetchSettled.finally(() => setIsSyncingAssets(false));
        }
      }

      // Merge in items already queued locally so a reload mid-queue doesn't
      // send the customer back to re-shoot a step they already captured.
      // Skipped when offline support is off — there's never anything queued.
      const queuedItemIds = offlineEnabledForThisFetch
        ? new Set(await offlineDb.getQueuedAssignmentItemIds(token))
        : new Set();
      if (offlineEnabledForThisFetch) hydrateCapturedFilesFromQueue();
      const isEffectivelyHandled = (item) =>
        item.status === 'completed' || item.status === 'skipped' || queuedItemIds.has(item.assignmentItemId);

      // Only handle navigation if skipNavigation is false
      if (!skipNavigation) {
        if (NOTES_SAVE_EXIT_ENABLED && getStoredSavedExitPending(token)) {
          goToPhase('saved-exit');
          return;
        }

        const storedPendingResume = NOTES_SAVE_EXIT_ENABLED ? getStoredResumeTarget(token) : null;
        if (storedPendingResume) {
          pendingResumeRef.current = storedPendingResume;
          goToPhase('resume-gate');
          return;
        }

        // A prior session on this link just lapsed (Save & Exit or idle
        // timeout) and this device is taking over — gate the resume behind
        // an explicit "Continue" click instead of silently jumping in.
        const resumeGate = Boolean(response.data.resumeGate);

        const applyResumeTarget = (targetPhase, targetStepIndex = null, multiPhotoItemId = null) => {
          if (resumeGate) {
            const target = { phase: targetPhase, stepIndex: targetStepIndex, multiPhotoItemId };
            pendingResumeRef.current = target;
            setStoredResumeTarget(token, target);
            goToPhase('resume-gate');
            return;
          }
          if (targetStepIndex !== null) setCurrentStepIndex(targetStepIndex);
          if (multiPhotoItemId) setMultiPhotoReviewItemId(multiPhotoItemId);
          commitPhase(targetPhase);
        };

        // Determine starting phase and step
        const hasCompletedItems = Object.keys(completedItems).length > 0 || queuedItemIds.size > 0;

        // Find first pending REQUIRED step (skip optional steps, and anything queued locally)
        const firstPendingRequiredIndex = fetchedItems.findIndex(item =>
          item.required && item.status === 'pending' && !queuedItemIds.has(item.assignmentItemId)
        );

        // Fallback: find any pending step if no required steps are pending
        const firstPendingIndex = fetchedItems.findIndex(item =>
          item.status === 'pending' && !queuedItemIds.has(item.assignmentItemId)
        );

        const lastCompletedIndex = fetchedItems.map((item, index) =>
          isEffectivelyHandled(item) ? index : -1
        ).filter(index => index !== -1).pop(); // Get the last completed/skipped/queued item index

        if (response.data.assignment.status === 'in_progress' && hasCompletedItems) {
          // Session recovery scenario
          setSessionRecovered(true);

          // Check if all required steps are completed
          const allRequiredCompleted = fetchedItems
            .filter(item => item.required)
            .every(isEffectivelyHandled);

          const multiPhotoReviewIdFor = (stepIndex) => {
            // On a resume-gate hand-off, always land the new device straight in the
            // camera — ResumeGate itself already showed everything captured so far,
            // so re-opening the gallery for one item right after Continue is
            // redundant (and was the multi-photo "lands on review, not camera" bug).
            if (resumeGate) return null;
            const item = fetchedItems[stepIndex];
            const maxPhotos = Number(item?.maxPhotos ?? 1);
            const media = completedMedia[item?.assignmentItemId];
            return (maxPhotos > 1 && media?.length > 0) ? item.assignmentItemId : null;
          };

          if (allRequiredCompleted) {
            // All required steps done. On a same-device resume, go straight to
            // review — Review.jsx already lets the customer capture any
            // remaining optional item from there via "Capture Now". But on a
            // resume-gate hand-off, jumping to review would skip the new
            // device/user past pending optional steps it never saw — land on
            // the next one instead, same as the required-pending case below.
            if (resumeGate && firstPendingIndex >= 0) {
              applyResumeTarget('capturing', firstPendingIndex, multiPhotoReviewIdFor(firstPendingIndex));
            } else {
              applyResumeTarget('review');
            }
          } else if (firstPendingRequiredIndex >= 0 && lastCompletedIndex !== undefined) {
            // Resume from last completed item (n-1) instead of next pending required (n) —
            // except on a resume-gate hand-off, where a new device/user should land on
            // their own next step to capture, not re-show the prior user's last step.
            // That next step must be the next PENDING step in order, required or not —
            // using firstPendingRequiredIndex here would silently skip over an optional
            // step the customer never saw (and never explicitly skipped via handleSkip).
            const nextPendingIndex = firstPendingIndex >= 0 ? firstPendingIndex : firstPendingRequiredIndex;
            const targetIndex = resumeGate ? nextPendingIndex : lastCompletedIndex;
            applyResumeTarget('capturing', targetIndex, multiPhotoReviewIdFor(targetIndex));
          } else if (firstPendingRequiredIndex >= 0) {
            // Fallback: Resume from first pending required item (unreachable via
            // resume-gate in practice — lastCompletedIndex is always defined here
            // since hasCompletedItems already guarantees a handled item exists).
            const targetIndex = resumeGate && firstPendingIndex >= 0 ? firstPendingIndex : firstPendingRequiredIndex;
            applyResumeTarget('capturing', targetIndex, multiPhotoReviewIdFor(targetIndex));
          } else if (firstPendingIndex >= 0) {
            // Only optional steps remaining
            applyResumeTarget('capturing', firstPendingIndex, multiPhotoReviewIdFor(firstPendingIndex));
          } else {
            // All items completed, go to review
            applyResumeTarget('review');
          }
        } else if (response.data.assignment.status === 'in_progress') {
          applyResumeTarget('capturing', firstPendingRequiredIndex >= 0 ? firstPendingRequiredIndex : (firstPendingIndex >= 0 ? firstPendingIndex : 0));
        } else if (response.data.assignment.requiredOtpVerification && !response.data.assignment.isOtpVerified) {
          applyResumeTarget('otp-verification');
        } else {
          applyResumeTarget('welcome');
        }
      }

      return completedItems;
    } catch (err) {
      // Handle session conflict specifically
      if (err.response?.status === 409 && err.response?.data?.sessionConflict) {
        setSessionConflictBranding(err.response.data.branding || null);
        setSessionConflictMessage(err.response.data.message || null);
        setShowSessionConflictModal(true);
        setLoading(false);
        return;
      }

      // Handle session-specific errors
      if (isSessionError(err)) {
        const userMessage = getErrorMessage(err);
        navigate('/error', {
          replace: true,
          state: {
            type: 'session_error',
            message: userMessage,
            branding: null,
            from: window.location.pathname + window.location.search
          },
        });
        return;
      }

      const data = err.response?.data;

      // Map HTTP status + error type to our error type keys
      let type = 'default';
      if (!navigator.onLine) {
        type = 'offline';
      } else if (err.response?.status === HTTP_STATUS_NOT_FOUND) {
        // 404: truly not found
        type = data?.type ?? 'not_found';
      } else if (err.response?.status === HTTP_STATUS_GONE) {
        // 410: gone — could be 'deleted', 'expired', 'completed', etc.
        type = data?.type ?? 'expired';
      } else if (data?.type) {
        type = data.type;
      } else {
        type = 'default';
      }

      // Terminal states can never be captured against again, so the local
      // queue is dead weight — clean it up. 'offline' is recoverable, so it's
      // excluded. 'expired' on an offline-enabled assignment is a special
      // case: the device may have kept capturing after the link expired in
      // the background without the user knowing, and the queue can now
      // legitimately sync that pre-expiry data (see capture.service.ts's
      // isOfflineReplay bypass) — so give it one chance to flush before
      // wiping what might still be unsynced work.
      let pendingAfterFlush = null;
      if (type === 'expired' && offlineSupportEnabled) {
        const pending = await offlineDb.getPendingMutations(token);
        if (pending.length > 0) {
          try {
            await captureSyncQueue.flushQueue(token, { onSyncError: () => {} });
          } catch (flushError) {
            console.error('Best-effort flush before expired-link wipe failed:', flushError);
          }
          pendingAfterFlush = await offlineDb.getPendingMutations(token);
        }
      }
      if (type !== 'offline' && (pendingAfterFlush === null || pendingAfterFlush.length === 0)) {
        offlineDb.deleteDatabase(token).catch(() => {});
      }

      // A background refresh failing while offline isn't fatal — keep
      // whatever local state we have instead of kicking the customer out.
      if (skipNavigation && type === 'offline') {
        console.error('Skipped background refresh while offline:', err);
        return;
      }

      navigate('/error', {
        replace: true,
        state: { type, branding: null, from: window.location.pathname + window.location.search },
      });
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
    // hydrateCapturedFilesFromQueue is declared below and would be used
    // before initialization if added to this deps array.
  }, [token, navigate, goToPhase, commitPhase, offlineSupportEnabled]);

  const fetchUpdatedMedia = useCallback(async (assignmentItemId) => {
    try {
      const response = await captureClient.get(`/${token}/media/${assignmentItemId}`);

      const normalizedMedia = normalizeMediaArray(response.data.media);
      if (normalizedMedia.length > 0) {
        if (offlineSupportEnabled) {
          assetCache.prefetchAssets(
            token,
            normalizedMedia
              .filter((m) => m?.url && !['video', 'audio'].includes(getMediaCategory(m.mimeType, m.fileName)))
              .map((m) => m.url)
          );
          // Video/audio get the bigger ceiling — see collectCompletedLargeMediaAssetUrls.
          assetCache.prefetchAssets(
            token,
            normalizedMedia
              .filter((m) => m?.url && ['video', 'audio'].includes(getMediaCategory(m.mimeType, m.fileName)))
              .map((m) => m.url),
            { maxBytes: REFERENCE_VIDEO_ASSET_MAX_BYTES }
          );
        }

        // Update completed items media with fresh TruVideo URL array
        setCompletedItemsMedia(prev => ({
          ...prev,
          [assignmentItemId]: normalizedMedia.map((mediaEntry) => ({
            ...mediaEntry,
            url: mediaEntry.url,
            fileName: mediaEntry.fileName,
            mimeType: mediaEntry.mimeType,
            fileSize: mediaEntry.fileSize,
            type: mediaEntry.type,
            completedAt: mediaEntry.completedAt,
            identifierText: mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
            customerIdentifierText: mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
            ocrIdentifierText: mediaEntry.identifierText,
            duration: parseInt(mediaEntry.duration, 10) || 0,
          })),
        }));

        // Update captured files to merge server media with the local photo array
        setCapturedFiles(prev => {
          const updated = { ...prev };
          const existingEntry = normalizeCapturedEntry(prev[assignmentItemId]);

          const mergePhoto = (existingPhoto, mediaEntry) => ({
            ...(existingPhoto || {}),
            ...mediaEntry,
            url: mediaEntry.url,
            uploadResult: null,
            previewUrl: existingPhoto?.previewUrl || mediaEntry.url,
            truVideoUrl: mediaEntry.url,
            uploaded: true,
            mediaFileId: mediaEntry.mediaFileId,
            identifierText: mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
            customerIdentifierText: mediaEntry.customerIdentifierText ?? mediaEntry.identifierText,
            ocrIdentifierText: mediaEntry.identifierText,
          });

          const usedMediaFileIds = new Set();
          const reordered = existingEntry.photos
            .map((photo) => {
              const mediaEntry = normalizedMedia.find(
                m => m.mediaFileId === photo.mediaFileId || m.mediaFileId === photo.uploadResult?.mediaId
              );
              if (!mediaEntry || usedMediaFileIds.has(mediaEntry.mediaFileId)) return null;
              usedMediaFileIds.add(mediaEntry.mediaFileId);
              return mergePhoto(photo, mediaEntry);
            })
            .filter(Boolean);

          const unmatched = normalizedMedia
            .filter(mediaEntry => !usedMediaFileIds.has(mediaEntry.mediaFileId))
            .map((mediaEntry, index) => mergePhoto(existingEntry.photos[index], mediaEntry));

          const mergedPhotos = [...reordered, ...unmatched];

          updated[assignmentItemId] = {
            ...existingEntry,
            ...prev[assignmentItemId],
            photos: mergedPhotos,
            uploaded: true,
          };
          return updated;
        });
      } else {
        setCompletedItemsMedia(prev => {
          const updated = { ...prev };
          delete updated[assignmentItemId];
          return updated;
        });
        setCapturedFiles(prev => {
          const updated = { ...prev };
          delete updated[assignmentItemId];
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to fetch updated media:', error);
    }
  }, [token, offlineSupportEnabled]);

  // Runs once an upload is confirmed, whether that happened immediately or via queued replay.
  const applyUploadSynced = useCallback((assignmentItemId) => {
    setReplaceUploadContext(null);
    setServerCompleted(prev => ({ ...prev, [assignmentItemId]: true }));
    setSessionCapturedItemIds(prev => {
      if (prev.has(assignmentItemId)) return prev;
      const updated = new Set(prev).add(assignmentItemId);
      setStoredSessionCapturedItemIds(token, updated);
      return updated;
    });
    setCaptureData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.assignmentItemId === assignmentItemId
          ? { ...item, status: 'completed' }
          : item
      )
    }));
    fetchUpdatedMedia(assignmentItemId);
  }, [token, fetchUpdatedMedia]);

  // Rebuilds capturedFiles from IndexedDB for anything still queued from a
  // previous page load — in-memory File objects don't survive a reload.
  const hydrateCapturedFilesFromQueue = useCallback(async () => {
    const pendingMedia = await offlineDb.getPendingMedia(token);
    if (pendingMedia.length === 0) return;

    setCapturedFiles(prev => {
      const updated = { ...prev };
      pendingMedia.forEach((media) => {
        const existingEntry = normalizeCapturedEntry(updated[media.assignmentItemId]);
        if (existingEntry.photos.some(p => p.localId === media.localId)) return; // already hydrated

        const file = new File([media.blob], media.fileName, { type: media.mimeType });
        const previewUrl = URL.createObjectURL(file);
        const photoEntry = {
          file,
          previewUrl,
          identifierText: media.customerIdentifierText ?? media.identifierText ?? null,
          customerIdentifierText: media.customerIdentifierText ?? null,
          ocrIdentifierText: media.identifierText ?? null,
          item: media.assignmentItem,
          uploaded: false,
          uploadResult: { queued: true, localId: media.localId, mediaId: null },
          mediaFileId: null,
          localId: media.localId,
          type: media.assignmentItem?.type,
        };
        updated[media.assignmentItemId] = {
          ...existingEntry,
          photos: [...existingEntry.photos, photoEntry],
          item: media.assignmentItem || existingEntry.item,
          uploaded: false,
        };
      });
      return updated;
    });
  }, [token]);

  // Shared bookkeeping helpers reused by both an immediate online success
  // and the sync queue's replay of a previously-queued (offline) mutation.
  const applyItemDeleted = useCallback((assignmentItemId) => {
    setCapturedFiles(prev => {
      const entry = prev[assignmentItemId];
      if (entry) {
        (entry.photos || []).forEach((photo) => {
          if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
        });
      }
      const updated = { ...prev };
      delete updated[assignmentItemId];
      return updated;
    });
    setServerCompleted(prev => {
      const updated = { ...prev };
      delete updated[assignmentItemId];
      return updated;
    });
    setCompletedItemsMedia(prev => {
      const updated = { ...prev };
      delete updated[assignmentItemId];
      return updated;
    });
    setCaptureData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.assignmentItemId === assignmentItemId ? { ...item, status: 'pending' } : item
      )
    }));
  }, []);

  const applyPhotoDeleted = useCallback((assignmentItemId, remainingPhotos) => {
    fetchUpdatedMedia(assignmentItemId);
    setServerCompleted(prev => {
      const updated = { ...prev };
      if (remainingPhotos > 0) updated[assignmentItemId] = true; else delete updated[assignmentItemId];
      return updated;
    });
    setCaptureData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.assignmentItemId === assignmentItemId
          ? { ...item, status: remainingPhotos > 0 ? 'completed' : 'pending' }
          : item
      )
    }));
  }, [fetchUpdatedMedia]);

  const applySkipped = useCallback((assignmentItemId, newStatus) => {
    setCaptureData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.assignmentItemId === assignmentItemId ? { ...item, status: newStatus } : item
      )
    }));
  }, []);

  // Apply sub-account favicon when capture data is loaded
  useEffect(() => {
    const faviconUrl = captureData?.tenant?.faviconUrl;
    if (!faviconUrl) { return; }

    const existing = document.querySelector("link[rel~='icon']");
    const link = existing || document.createElement('link');
    link.rel = 'icon';
    // Offline (or nothing left in the browser's own cache) — fall back to
    // whatever assetCache prefetched for this favicon while online. Never
    // touches IndexedDB when offline support is off.
    link.onerror = offlineSupportEnabled ? async () => {
      const blob = await assetCache.getCachedAssetBlob(token, faviconUrl);
      if (blob) link.href = URL.createObjectURL(blob);
    } : null;
    link.href = faviconUrl;
    if (!existing) {
      document.head.appendChild(link);
    }

    // Restore default favicon on unmount
    return () => {
      link.onerror = null;
      link.href = '/favicon.ico';
    };
  }, [captureData?.tenant?.faviconUrl, token, offlineSupportEnabled]);

  useEffect(() => {
    fetchCaptureData();
  }, [fetchCaptureData]);

  // Handler for starting a new session from modal
  // const handleStartNewSession = async () => {
  //   setShowSessionConflictModal(false);
  //   setSessionConflictMessage(null);
  //   resetExpiryReminders();
  //   await fetchCaptureData(false, true);
  // };

  // Handler for canceling session conflict modal
  const handleCancelSessionConflict = () => {
    setShowSessionConflictModal(false);
    setSessionConflictMessage(null);
    navigate('/error', {
      replace: true,
      state: {
        type: 'session_error',
        message: 'This link has been opened on another device',
        branding: sessionConflictBranding,
        from: window.location.pathname + window.location.search
      }
    });
  };

  // Block the Welcome screen behind the name/email gate as soon as it lands —
  // before this, closing the tab left an "active" session with no way to
  // tell who had opened the link. Skipped when the initial fetch already
  // came back as a session conflict (another device holds the link) — that
  // should restrict the device outright, not ask for a name/email first.
  useEffect(() => {
    if (phase === 'welcome' && !loading && pendingPhase === null && !showSessionConflictModal && !hasCaptureIdentity(token)) {
      setPendingPhase('welcome');
    }
  }, [phase, loading, pendingPhase, token, showSessionConflictModal]);

  // Call start API when landing on welcome, or when landing on resume-gate
  // (a device picking up a lapsed session) — gated on identity so nothing is
  // marked "opened"/claimed until we actually know who's using the link.
  // resume-gate already only becomes the active phase once identity exists
  // (see goToPhase('resume-gate') above), so this fires immediately on
  // landing there, before the user clicks Continue — the GET :token call
  // only peeks at a lapsed session (see server capture-session.service.ts),
  // this /start call is what actually claims it for this device.
  useEffect(() => {
    const markAsStarted = async () => {
      if ((phase === 'welcome' || phase === 'resume-gate') && !startedRef.current && !loading && pendingPhase === null && hasCaptureIdentity(token)) {
        startedRef.current = true;
        try {
          const identity = getNoteIdentity(token);
          await captureClient.post(`/${token}/start`, {
            linkHolderName: identity?.name,
            linkHolderEmail: identity?.email,
          });

          // Request location first
          const loc = await requestLocation();
          setLocation(loc);

          // Capture provenance: assignment_opened (only once per browser per assignment)
          const provenanceKey = `${token}_provenance_captured`;
          if (captureData?.workflow?.captureProvenanceDetails && !localStorage.getItem(provenanceKey)) {
            const metadata = await captureProvenanceMetadata(loc);
            await captureSyncQueue.queueItemMutation({
              token,
              type: 'provenance',
              assignmentItemId: 'assignment',
              payload: { eventType: 'assignment_opened', metadata },
              offlineSupportEnabled,
            });
            localStorage.setItem(provenanceKey, 'true');
          }
        } catch (err) {
          console.error('Failed to mark as opened:', err);
        }
      }
    };

    markAsStarted();
  }, [phase, loading, token, captureData, pendingPhase, offlineSupportEnabled]);

  // Revoke blob URLs on unmount only — read via a ref so this effect
  // doesn't re-run (and revoke still-in-use URLs) on every capture.
  const capturedFilesRef = useRef(capturedFiles);
  useEffect(() => {
    capturedFilesRef.current = capturedFiles;
  }, [capturedFiles]);

  useEffect(() => {
    return () => {
      Object.values(capturedFilesRef.current).forEach((entry) => {
        (entry?.photos || []).forEach((photo) => {
          if (photo.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(photo.previewUrl);
          }
        });
      });
    };
  }, []);

  // Best-effort cleanup of abandoned offline queues from past assignments.
  useEffect(() => {
    offlineDb.sweepStaleDatabases();
  }, []);

  // Offline support off: there's no queue to fall back on, so losing
  // connectivity mid-session should behave exactly like before offline
  // support existed — send the customer straight to the error page.
  useEffect(() => {
    if (offlineSupportEnabled) return undefined;
    const handleOffline = () => {
      navigate('/error', {
        replace: true,
        state: {
          type: 'offline',
          branding: captureData?.workflow?.branding,
          from: window.location.pathname + window.location.search
        },
      });
    };
    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, [navigate, captureData, offlineSupportEnabled]);

  // Subscribe to the offline queue's pending-item count for the status banner.
  // Skipped entirely when offline support is off — there's never a queue to watch.
  useEffect(() => {
    if (!token || !offlineSupportEnabled) return undefined;
    return captureSyncQueue.subscribeQueue(token, (count) => {
      // Everything that was queued has now synced with no errors along the
      // way — surface that once, rather than leaving the customer to infer
      // it from the banner quietly disappearing.
      if (prevPendingSyncCountRef.current > 0 && count === 0 && !syncHadErrorRef.current) {
        setSyncStatus({ status: 'success', message: 'All your data has been synced.' });
      }
      if (count === 0) {
        syncHadErrorRef.current = false;
      }
      prevPendingSyncCountRef.current = count;
      setPendingSyncCount(count);
    });
  }, [token, offlineSupportEnabled]);

  // Subscribe to whether the queue is actively replaying right now, to
  // drive the blocking sync overlay. Separate from pendingSyncCount above —
  // that stays > 0 even once a pass has already stopped retrying after a
  // real error, which must not leave this overlay stuck open.
  useEffect(() => {
    if (!token || !offlineSupportEnabled) return undefined;
    return captureSyncQueue.subscribeFlushing(token, setIsSyncingData);
  }, [token, offlineSupportEnabled]);

  // Auto-dismiss the one-off sync result toast.
  useEffect(() => {
    if (!syncStatus) return undefined;
    const timeout = setTimeout(() => setSyncStatus(null), syncStatus.status === 'error' ? 6000 : 4000);
    return () => clearTimeout(timeout);
  }, [syncStatus]);

  // Reset any reminders already shown when a different device/technician
  // hands off onto this link — see useExpiryReminders' resetReminders doc.
  useEffect(() => {
    if (phase === 'resume-gate') {
      resetExpiryReminders();
      setShowExpiredOfflineAlert(false);
    }
  }, [phase, resetExpiryReminders]);

  // Flush the offline queue whenever we come online, and once on mount in
  // case items were queued in a previous page load. Skipped when offline
  // support is off. The browser's `online` event can fire before the
  // network path is actually usable (a WebKit/Safari reconnect race), so
  // we confirm real reachability first instead of firing the upload
  // straight off `online` — see waitForRealConnectivity.
  useEffect(() => {
    if (!token || !isOnline || !offlineSupportEnabled) return;
    let cancelled = false;

    const runFlush = async () => {
      await captureSyncQueue.waitForRealConnectivity();
      if (cancelled) return;
      captureSyncQueue.flushQueue(token, {
        reconcile: () => fetchCaptureData(true),
        onMutationSynced: (info) => {
          if (info.type === 'upload') {
            setCapturedFiles(prev => {
              const existingEntry = normalizeCapturedEntry(prev[info.assignmentItemId]);
              const updatedPhotos = existingEntry.photos.map(photo =>
                photo.localId === info.mediaLocalId
                  ? { ...photo, uploaded: true, uploadResult: info.result, mediaFileId: info.result?.mediaId || null }
                  : photo
              );
              return { ...prev, [info.assignmentItemId]: { ...existingEntry, photos: updatedPhotos } };
            });
            applyUploadSynced(info.assignmentItemId);
          } else if (info.type === 'deleteItem') {
            applyItemDeleted(info.assignmentItemId);
          } else if (info.type === 'deletePhoto') {
            applyPhotoDeleted(info.assignmentItemId, info.result?.remainingPhotos ?? 0);
          } else if (info.type === 'skip') {
            applySkipped(info.assignmentItemId, info.result?.alreadyCaptured ? 'completed' : 'skipped');
          } else if (info.type === 'complete') {
            setCaptureData(prev => ({ ...prev, completedItemsCount: info.result?.completedItemsCount }));
            localStorage.removeItem(`${token}_provenance_captured`);
            offlineDb.deleteDatabase(token).catch(() => {});
            setPhase('done');
          } else if (info.type === 'saveAndExit') {
            // The customer already moved past SavedExit while this was queued —
            // only now that the server has actually closed the session is it
            // safe to release the local cookie too. SavedExit's own click-time
            // guard (pendingSyncCount > 0) means nothing else was left queued
            // when the user tapped Save, so this queued saveAndExit syncing is
            // the last outstanding item — safe to clear the local queue/cache
            // now, same as the 'complete' branch above. If the customer resumes
            // this link again, everything simply re-caches from the server.
            deleteSessionToken(token);
            offlineDb.deleteDatabase(token).catch(() => {});
          }
          // 'beginCapture' / 'start' / 'provenance' — no bookkeeping needed, just consumed the retry.
        },
        onSessionExpiredUnrecoverable: () => {
          setSubmitError('Your session could not be refreshed after being offline for a while. Please reload the page to continue.');
        },
        onSyncError: ({ type, error }) => {
          syncHadErrorRef.current = true;
          const label = SYNC_MUTATION_LABELS[type] || 'A change';
          setSyncStatus({ status: 'error', message: `${label} couldn't be synced: ${getErrorMessage(error)}` });
        },
      });
    };

    runFlush();
    return () => { cancelled = true; };
  }, [token, isOnline, offlineSupportEnabled, fetchCaptureData, applyUploadSynced, applyItemDeleted, applyPhotoDeleted, applySkipped]);

  // The expiry countdown hit zero while offline (see handleExpiryReached
  // above) — rather than waiting for the next incidental API call to
  // discover the link is dead, proactively re-fetch the moment we're back
  // online so the existing expired-link handling (best-effort queue flush,
  // then redirect — see fetchCaptureData's catch block) kicks in right away.
  //
  // Deliberately doesn't fetch immediately when there's queued data: the
  // reconnect-flush effect above reacts to this same `isOnline` flip and is
  // already replaying the queue (now able to succeed even past expiry, via
  // the offline-replay bypass) — racing ahead of it here would yank the
  // user to the Link Expired page while their own sync is still visibly in
  // progress, even though it's about to succeed. Instead, wait for that
  // flush to actually conclude (success or failure — subscribeFlushing
  // reports both) before checking the link's real state, so the existing
  // "Data Sync" overlay gets to run to completion first. A bounded fallback
  // still fires this if a flush never starts at all, so this never leaves
  // the user waiting with no feedback if something else goes wrong.
  useEffect(() => {
    if (!(expiryReachedRef.current && isOnline)) return undefined;
    expiryReachedRef.current = false;

    let settled = false;
    let unsubscribeFlushing;
    let fallbackTimer;

    const proceed = () => {
      if (settled) return;
      settled = true;
      unsubscribeFlushing?.();
      clearTimeout(fallbackTimer);
      fetchCaptureData(true);
    };

    (async () => {
      const pending = await offlineDb.getPendingMutations(token);
      if (settled) return;

      if (pending.length === 0 && !captureSyncQueue.isQueueFlushing(token)) {
        proceed();
        return;
      }

      let sawFlushing = captureSyncQueue.isQueueFlushing(token);
      unsubscribeFlushing = captureSyncQueue.subscribeFlushing(token, (isFlushingNow) => {
        if (isFlushingNow) {
          sawFlushing = true;
          return;
        }
        if (sawFlushing) proceed();
      });

      fallbackTimer = setTimeout(proceed, 20000);
    })();

    return () => {
      settled = true;
      unsubscribeFlushing?.();
      clearTimeout(fallbackTimer);
    };
  }, [isOnline, fetchCaptureData, token]);

  // If the customer tried to submit while items were still syncing, retry
  // automatically the moment the queue drains.
  useEffect(() => {
    if (pendingSubmitRef.current && pendingSyncCount === 0 && phase === 'review') {
      pendingSubmitRef.current = false;
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSyncCount]);

  // Show conflict modal when a mid-session 409 fires during any POST/DELETE call
  // (e.g. upload, start). The captureClient interceptor dispatches this event
  // so the user is clearly informed their session is active on another device.
  useEffect(() => {
    const handleMidSessionConflict = (event) => {
      setSessionConflictMessage(event.detail?.message || null);
      setShowSessionConflictModal(true);
    };
    window.addEventListener('capture-session-conflict', handleMidSessionConflict);
    return () => window.removeEventListener('capture-session-conflict', handleMidSessionConflict);
  }, []);

  const handleViewInstructions = () => {
    goToPhase('instructions');
  };

  const handleStart = async () => {
    try {
      // Mark assignment as in_progress — queued and retried automatically
      // if this fails because the device is offline.
      const identity = getNoteIdentity(token);
      await captureSyncQueue.queueItemMutation({
        token,
        type: 'beginCapture',
        assignmentItemId: 'assignment',
        payload: { linkHolderName: identity?.name, linkHolderEmail: identity?.email },
        offlineSupportEnabled,
      });
    } catch (err) {
      console.error('Failed to mark assignment as in_progress:', err);
      if (err.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        navigate('/error', {
          replace: true,
          state: { type: 'default', branding: captureData?.workflow?.branding, from: window.location.pathname + window.location.search },
        });
        return;
      }
      // Continue with capture flow even if status update fails
    }

    setCurrentStepIndex(0);
    resumeMultiPhotoReviewIfApplicable(0);
    setPhase('capturing');
  };

  // Called when the user taps Continue on the ResumeGate screen — applies the
  // navigation target that was stashed when the backend signaled a hand-off
  // from a lapsed prior session.
  const handleResumeContinue = () => {
    const target = pendingResumeRef.current;
    pendingResumeRef.current = null;
    clearStoredResumeTarget(token);

    if (!target) {
      setPhase('welcome');
      return;
    }
    if (target.stepIndex !== null) setCurrentStepIndex(target.stepIndex);
    if (target.multiPhotoItemId) setMultiPhotoReviewItemId(target.multiPhotoItemId);

    commitPhase(target.phase);
  };

  // Save & Exit — shows a read-only review of everything captured so far.
  // Only called after any pending capture/file has already uploaded
  // successfully (see MediaUploader/FileUploadPane), so in-flight work is
  // never silently lost. The session itself isn't released until the user
  // confirms via handleConfirmSaveAndExit below.
  const handleSaveAndExit = () => {
    // Clear any lingering "mid multi-photo review" flag — Save & Exit can be
    // triggered from that sub-screen directly (before "Next"), and leaving it
    // set would make Capturing wrongly reopen MultiPhotoReview instead of the
    // camera if the user retakes this item from the SavedExit screen.
    setMultiPhotoReviewItemId(null);
    setStoredSavedExitPending(token);
    setPhase('saved-exit');
  };

  // Called when the user taps Save on the SavedExit review screen — this is
  // what actually releases the session so another device can take over
  // immediately instead of waiting for the idle window to lapse. Queued and
  // retried automatically if this fails because the device is offline; a
  // genuine (non-network) rejection propagates to SavedExit so it can show an
  // error instead of falsely telling the customer their progress was saved.
  //
  // The local session cookie is only cleared once the server has actually
  // closed the session: immediately here if the call went through live, or
  // later via onMutationSynced (below) once a queued replay lands. The
  // server requires the still-active session header to close a session, so
  // clearing the cookie up front would strand a queued replay with no
  // session to present once connectivity returns.
  const handleConfirmSaveAndExit = async () => {
    const { queued } = await captureSyncQueue.queueItemMutation({
      token,
      type: 'saveAndExit',
      assignmentItemId: 'assignment',
      offlineSupportEnabled,
    });
    if (!queued) {
      deleteSessionToken(token);
      // Went through live, and SavedExit's own click-time guard
      // (pendingSyncCount > 0) already ensured nothing else was left queued
      // when the user tapped Save — so everything captured this session is
      // now confirmed on the server. Safe to drop the local queue/cache
      // rather than let it sit on this device indefinitely; a future resume
      // of this link just re-caches from the server instead.
      offlineDb.deleteDatabase(token).catch(() => {});
    }
  };

  // Called when user captures media for the current step
  const handleCapture = async (file, identifierText, uploadResult) => {
    const allStepItems = captureData.items;
    const currentItem = allStepItems[currentStepIndex];
    const previewUrl = URL.createObjectURL(file);

    // Already consumed for this capture — clear now so it can't leak into the next one.
    setReplaceUploadContext(null);

    // Queued-but-not-synced uploads are truthy but not a real success yet.
    const isSynced = !!uploadResult && !uploadResult.queued;

    const photoEntry = {
      file,
      previewUrl,
      identifierText,
      customerIdentifierText: uploadResult?.customerIdentifierText ?? identifierText,
      ocrIdentifierText: uploadResult?.identifierText ?? null,
      item: currentItem,
      uploaded: isSynced,
      uploadResult: uploadResult || null,
      mediaFileId: uploadResult?.mediaId || null,
      localId: uploadResult?.localId || null,
      type: currentItem.type,
    };

    const retakeIndex =
      retakePhotoIndexRef.current?.assignmentItemId === currentItem.assignmentItemId
        ? retakePhotoIndexRef.current.index
        : null;
    retakePhotoIndexRef.current = null;

    setCapturedFiles(prev => {
      const existingEntry = normalizeCapturedEntry(prev[currentItem.assignmentItemId]);
      const serverPhotosForItem = Array.isArray(completedItemsMedia[currentItem.assignmentItemId])
        ? completedItemsMedia[currentItem.assignmentItemId]
        : [];
      const basePhotos = existingEntry.photos.length > 0 ? existingEntry.photos : serverPhotosForItem;
      const updatedPhotos =
        retakeIndex !== null && retakeIndex >= 0 && retakeIndex < basePhotos.length
          ? basePhotos.map((p, i) => (i === retakeIndex ? photoEntry : p))
          : [...basePhotos, photoEntry];
      return {
        ...prev,
        [currentItem.assignmentItemId]: {
          ...existingEntry,
          photos: updatedPhotos,
          item: currentItem,
          uploaded: isSynced,
        }
      };
    });

    // If uploaded successfully (not just queued for later), mark as server
    // completed and fetch updated media.
    if (isSynced) {
      applyUploadSynced(currentItem.assignmentItemId);
    }

    // Clear retake mode when capturing new item (not from retake)
    if (retakeItemIdRef.current !== currentItem.assignmentItemId) {
      retakeItemIdRef.current = null;
    }

    // If we came here from "Add more" in MultiPhotoReview, go back to MultiPhotoReview
    if (addMorePhotoItemIdRef.current === currentItem.assignmentItemId) {
      addMorePhotoItemIdRef.current = null;
      retakeItemIdRef.current = null;
      retakeFromMultiPhotoReviewRef.current = false;
      setRetakeItemId(null);
      setMultiPhotoReviewItemId(currentItem.assignmentItemId);
      return;
    }

    const isRetakeFromMultiPhotoReview = retakeItemIdRef.current === currentItem.assignmentItemId && retakeFromMultiPhotoReviewRef.current;
    const isRetakeFromReview = retakeItemIdRef.current === currentItem.assignmentItemId && !retakeFromMultiPhotoReviewRef.current;

    // If we came here from "Retake" in review (or Save & Exit), go back to
    // whichever screen it was — see retakeReturnPhaseRef.
    if (isRetakeFromReview) {
      retakeItemIdRef.current = null;
      retakeFromMultiPhotoReviewRef.current = false;
      setRetakeItemId(null);
      await fetchCaptureData(true);
      setPhase(retakeReturnPhaseRef.current);
      return;
    }

    // If we came here from "Retake" in MultiPhotoReview, go back to MultiPhotoReview
    if (isRetakeFromMultiPhotoReview) {
      retakeItemIdRef.current = null;
      retakeFromMultiPhotoReviewRef.current = false;
      setRetakeItemId(null);
      setMultiPhotoReviewItemId(currentItem.assignmentItemId);
      return;
    }

    const maxPhotos = Number(currentItem.maxPhotos ?? 1);

    if (addPhotoItemIdRef.current === currentItem.assignmentItemId) {
      addPhotoItemIdRef.current = null;
      await fetchCaptureData(true);
      setPhase(retakeReturnPhaseRef.current);
      return;
    }

    // For multi-photo steps: after each capture go to MultiPhotoReview
    if (maxPhotos > 1) {
      setMultiPhotoReviewItemId(currentItem.assignmentItemId);
      return;
    }
    if (currentStepIndex < allStepItems.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      resumeMultiPhotoReviewIfApplicable(nextIndex);
    } else {
      // We're at the last step, refresh data and go to review
      await fetchCaptureData(true);
      setPhase('review');
    }
  };

  const clearRetakeState = () => {
    retakeItemIdRef.current = null;
    retakeFromMultiPhotoReviewRef.current = false;
    retakePhotoIndexRef.current = null;
    retakeMediaFileIdRef.current = null;
    setReplaceUploadContext(null);
    setRetakeItemId(null);
  };

  const handleSkip = async () => {
    const allStepItems = captureData.items;
    const currentItem = allStepItems[currentStepIndex];

    if (!currentItem.required) {
      try {
        const { queued, data } = await captureSyncQueue.queueItemMutation({
          token,
          type: 'skip',
          assignmentItemId: currentItem.assignmentItemId,
          offlineSupportEnabled,
        });
        // Offline: best guess until the queue flushes and confirms via
        // onMutationSynced; online: the real server answer.
        const newStatus = queued ? 'skipped' : (data?.alreadyCaptured ? 'completed' : 'skipped');
        applySkipped(currentItem.assignmentItemId, newStatus);
      } catch (err) {
        console.error('Failed to skip item on server:', err);
      }
    }

    // If we're in retake mode and user skips, clear all retake bookkeeping
    if (retakeItemIdRef.current === currentItem.assignmentItemId) {
      clearRetakeState();
      // Refresh data from server before going back to review (or Save & Exit)
      await fetchCaptureData(true);
      setPhase(retakeReturnPhaseRef.current);
      return;
    }

    if (currentStepIndex < allStepItems.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      resumeMultiPhotoReviewIfApplicable(nextIndex);
    } else {
      // Refresh data from server before going to review
      await fetchCaptureData(true);
      setPhase('review');
    }
  };

  const handleDelete = async (assignmentItemId) => {
    // Nothing confirmed on the server yet for this item — pure local
    // cleanup, no network call at all (and cancel anything queued for it).
    if (!serverCompleted[assignmentItemId]) {
      const entry = capturedFiles[assignmentItemId];
      (entry?.photos || []).forEach((photo) => {
        if (photo.localId) captureSyncQueue.cancelQueuedUpload(token, photo.localId);
      });
      applyItemDeleted(assignmentItemId);
      return;
    }

    try {
      await captureSyncQueue.queueItemMutation({
        token,
        type: 'deleteItem',
        assignmentItemId,
        offlineSupportEnabled,
      });
      // Optimistic either way — confirmed for real if this was queued once
      // the sync engine replays it and calls applyItemDeleted again (a
      // second, idempotent local cleanup) via onMutationSynced.
      applyItemDeleted(assignmentItemId);
    } catch (error) {
      console.error('Failed to delete item:', error);
      if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        navigate('/error', {
          replace: true,
          state: { type: 'default', branding: captureData?.workflow?.branding, from: window.location.pathname + window.location.search },
        });
        return;
      }
      setSubmitError('Failed to delete item. Please try again.');
    }
  };

  const handleDeletePhoto = async (assignmentItemId, mediaFileId, localId) => {
    if (!mediaFileId) {
      // Never synced — pure local cleanup, and cancel the queued upload
      // for it (if any) so it never gets sent.
      const existingEntry = normalizeCapturedEntry(capturedFiles[assignmentItemId]);
      let removeIndex = localId
        ? existingEntry.photos.findIndex((p) => !p.uploaded && p.localId === localId)
        : -1;
      if (removeIndex === -1) {
        // Fallback for the rare case a caller couldn't supply localId —
        // matches previous behavior rather than silently no-op'ing.
        for (let idx = existingEntry.photos.length - 1; idx >= 0; idx -= 1) {
          if (!existingEntry.photos[idx].uploaded) {
            removeIndex = idx;
            break;
          }
        }
      }

      if (removeIndex >= 0) {
        const removedPhoto = existingEntry.photos[removeIndex];
        if (removedPhoto?.localId) {
          await captureSyncQueue.cancelQueuedUpload(token, removedPhoto.localId);
        }
        if (removedPhoto?.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(removedPhoto.previewUrl);
        }
        const remainingLocalPhotos = existingEntry.photos.filter((_, idx) => idx !== removeIndex);
        setCapturedFiles(prev => {
          const updated = { ...prev };
          if (remainingLocalPhotos.length > 0) {
            updated[assignmentItemId] = { ...existingEntry, photos: remainingLocalPhotos, uploaded: false };
          } else {
            delete updated[assignmentItemId];
          }
          return updated;
        });
      }

      setServerCompleted(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
      setCaptureData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.assignmentItemId === assignmentItemId
            ? { ...item, status: 'pending' }
            : item
        )
      }));
      return;
    }

    try {
      const { queued, data } = await captureSyncQueue.queueItemMutation({
        token,
        type: 'deletePhoto',
        assignmentItemId,
        payload: { mediaFileId },
        offlineSupportEnabled,
      });
      if (!queued) {
        await applyPhotoDeleted(assignmentItemId, data?.remainingPhotos ?? 0);
      }
      // If queued, applyPhotoDeleted runs later via onMutationSynced once
      // the sync engine confirms the real remaining-photo count.
    } catch (error) {
      console.error('Failed to delete photo:', error);
      setSubmitError('Failed to delete photo. Please try again.');
    }
  };

  const handleAddPhoto = (assignmentItemId) => {
    const idx = captureData.items.findIndex(item => item.assignmentItemId === assignmentItemId);
    if (idx < 0) return;

    retakeReturnPhaseRef.current = phase === 'saved-exit' ? 'saved-exit' : 'review';
    addPhotoItemIdRef.current = assignmentItemId;
    retakeItemIdRef.current = null;
    retakeFromMultiPhotoReviewRef.current = false;
    retakePhotoIndexRef.current = null;
    setMultiPhotoReviewItemId(null);
    setServerCompleted(prev => {
      const updated = { ...prev };
      delete updated[assignmentItemId];
      return updated;
    });
    setRetakeItemId(assignmentItemId);
    setCurrentStepIndex(idx);
    setPhase('capturing');
  };

  // From review screen (or Save & Exit): retake a specific item
  const handleRetake = (assignmentItemId, fromReview = true, mediaFileId = null, photoIndex = null) => {
    retakeReturnPhaseRef.current = phase === 'saved-exit' ? 'saved-exit' : 'review';

    // Multi-photo per-slot retake: replace just the photo at photoIndex,
    // leaving the rest of the item's photos untouched. Must branch on
    // photoIndex, not mediaFileId — an unsynced offline photo has no
    // mediaFileId, and treating it as a whole-item retake would wipe every
    // other photo in the step.
    if (photoIndex !== null) {
      retakeItemIdRef.current = assignmentItemId;
      retakeFromMultiPhotoReviewRef.current = false;
      retakePhotoIndexRef.current = { assignmentItemId, index: photoIndex };

      if (mediaFileId) {
        // Already synced — tell the server which specific media file to replace.
        retakeMediaFileIdRef.current = { assignmentItemId, mediaFileId };
        setReplaceUploadContext({ replaceExisting: true, replaceMediaFileId: mediaFileId });
      } else {
        // Never synced — nothing to replace server-side; cancel its queued
        // upload so the stale capture never gets sent.
        const existingEntry = normalizeCapturedEntry(capturedFiles[assignmentItemId]);
        const stalePhoto = existingEntry.photos[photoIndex];
        if (stalePhoto?.localId) {
          captureSyncQueue.cancelQueuedUpload(token, stalePhoto.localId);
        }
        retakeMediaFileIdRef.current = null;
        setReplaceUploadContext(null);
      }

      setRetakeItemId(assignmentItemId);
      setCurrentStepIndex(captureData.items.findIndex(i => i.assignmentItemId === assignmentItemId));
      setPhase('capturing');
      return;
    }

    retakePhotoIndexRef.current = null;
    const allStepItems = captureData.items;
    const idx = allStepItems.findIndex(i => i.assignmentItemId === assignmentItemId);

    // Clear server completed status if retaking an already uploaded item
    if (serverCompleted[assignmentItemId]) {
      setServerCompleted(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
    }

    // Clear completed media if exists
    if (completedItemsMedia[assignmentItemId]) {
      setCompletedItemsMedia(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
    }

    // ALSO clear captured files for this item — and cancel any queued
    // (not-yet-synced) uploads under it so a stale capture is never sent.
    if (capturedFiles[assignmentItemId]) {
      const entryBeingCleared = normalizeCapturedEntry(capturedFiles[assignmentItemId]);
      entryBeingCleared.photos.forEach((photo) => {
        if (photo.localId && !photo.uploaded) {
          captureSyncQueue.cancelQueuedUpload(token, photo.localId);
        }
      });
      setCapturedFiles(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
    }

    // Only set retakeItemIdRef if coming from review screen
    if (fromReview) {
      retakeItemIdRef.current = assignmentItemId;
      retakeFromMultiPhotoReviewRef.current = false;
      setReplaceUploadContext({ replaceExisting: true });
      setRetakeItemId(assignmentItemId);
    }

    setCurrentStepIndex(idx);
    setPhase('capturing');
  };

  const handleSubmit = async () => {
    setSyncBlockedNotice(false);

    const requiredItems = captureData.items.filter(item => item.required);
    // Counts an item as "captured" once it has any photo locally — synced or
    // still queued/mid-sync — matching Review.jsx's allRequiredCaptured check
    // (which gates the button's disabled state). Previously this only counted
    // already-synced photos (photo.uploaded), so a required item captured
    // while offline — queued but not yet synced — was misreported here as
    // "missing" and showed "Please complete all required items" instead of
    // falling through to the pendingSyncCount check below.
    const uploadedItems = Object.values(capturedFiles).filter(cf => cf.uploaded || cf.photos?.length > 0);
    const serverCompletedCount = Object.keys(serverCompleted).length;

    if (requiredItems.length > uploadedItems.length + serverCompletedCount) {
      setSubmitError('Please complete all required items before submitting.');
      return;
    }

    // Some captures are still queued (offline or mid-sync) — wait for them
    // rather than completing prematurely. Auto-retries once the queue drains.
    if (pendingSyncCount > 0) {
      pendingSubmitRef.current = true;
      setSyncBlockedNotice(true);
      return;
    }

    setSubmitError('');

    try {
      // Just mark assignment as completed - files already uploaded
      const { queued, data } = await captureSyncQueue.queueItemMutation({
        token,
        type: 'complete',
        assignmentItemId: 'assignment',
        offlineSupportEnabled,
      });

      if (queued) {
        setSubmitError("You appear to be offline — this will submit automatically once you're back online.");
        return;
      }

      // Store the completed items count from API response
      const completedItemsCount = data?.completedItemsCount;

      // Update captureData to include the completed count for Done component
      setCaptureData(prev => ({
        ...prev,
        completedItemsCount
      }));

      localStorage.removeItem(`${token}_provenance_captured`);
      offlineDb.deleteDatabase(token).catch(() => {});
      setPhase('done');
    } catch (err) {
      console.error('Failed to complete assignment:', err);
      if (err.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        navigate('/error', {
          replace: true,
          state: { type: 'default', branding: captureData?.workflow?.branding, from: window.location.pathname + window.location.search },
        });
        return;
      }
      setSubmitError(err.response?.data?.message || 'Failed to complete assignment. Please try again.');
      setPhase('review');
    }
  };

  const handleMultiPhotoAddMore = () => {
    const currentItem = captureData.items[currentStepIndex];
    addMorePhotoItemIdRef.current = currentItem.assignmentItemId;
    retakeItemIdRef.current = currentItem.assignmentItemId;
    retakeFromMultiPhotoReviewRef.current = false;
    retakePhotoIndexRef.current = null;
    setRetakeItemId(currentItem.assignmentItemId);
    setMultiPhotoReviewItemId(null);
  };

  const handleMultiPhotoRetake = (assignmentItemId, photo, photoIndex) => {
    retakePhotoIndexRef.current = { assignmentItemId, index: photoIndex };
    if (photo.mediaFileId) {
      retakeMediaFileIdRef.current = { assignmentItemId, mediaFileId: photo.mediaFileId };
      setReplaceUploadContext({ replaceExisting: true, replaceMediaFileId: photo.mediaFileId });
    } else {
      // Never synced — cancel its queued upload (if any) so retaking it
      // never races a stale send.
      if (photo.localId) {
        captureSyncQueue.cancelQueuedUpload(token, photo.localId);
      }
      setReplaceUploadContext(null);
    }
    retakeItemIdRef.current = assignmentItemId;
    retakeFromMultiPhotoReviewRef.current = true;
    setRetakeItemId(assignmentItemId);
    setMultiPhotoReviewItemId(null);
  };

  const handleMultiPhotoNext = async () => {
    const allStepItems = captureData.items;
    setRetakeItemId(null);
    setMultiPhotoReviewItemId(null);
    if (currentStepIndex < allStepItems.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      resumeMultiPhotoReviewIfApplicable(nextIndex);
    } else {
      await fetchCaptureData(true);
      setPhase('review');
    }
  };

  const handleMultiPhotoBack = () => {
    retakePhotoIndexRef.current = null;
    setRetakeItemId(null);
    setMultiPhotoReviewItemId(null);
    handlePreviousStep();
  };

  // Show the MultiPhotoReview gallery when navigating to a multi-photo step
  // that already has photos, instead of the plain capture screen.
  const resumeMultiPhotoReviewIfApplicable = (stepIndex) => {
    const item = captureData?.items?.[stepIndex];
    if (!item) return;
    const maxPhotos = Number(item.maxPhotos ?? 1);
    if (maxPhotos <= 1) return;
    const localPhotoCount = capturedFiles[item.assignmentItemId]?.photos?.length || 0;
    const serverPhotoCount = completedItemsMedia[item.assignmentItemId]?.length || 0;
    if (localPhotoCount > 0 || serverPhotoCount > 0) {
      setMultiPhotoReviewItemId(item.assignmentItemId);
    }
  };

  const handlePreviousStep = () => {
    const currentItem = captureData?.items?.[currentStepIndex];
    if (currentItem && retakeItemIdRef.current === currentItem.assignmentItemId) {
      clearRetakeState();
    }
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      resumeMultiPhotoReviewIfApplicable(prevIndex);
    } else {
      setPhase('instructions');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base-content text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // If there's nothing to show at all, bail out
  if (!captureData && !showSessionConflictModal) { return null; }

  const assignment = captureData?.assignment;
  const workflow = captureData?.workflow;
  const tenant = captureData?.tenant;
  const items = captureData?.items;
  const branding = workflow?.branding || {};
  const pageBgStyle = { background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)' };
  const brandSolidStyle = branding.primaryColor ? { backgroundColor: branding.primaryColor } : null;
  const welcomeBgClass = 'bg-base-200';
  const accentBgClass = 'bg-primary';

  // Phase routing
  const renderPhase = () => {
    switch (phase) {
      case 'resume-gate':
        return (
          <ResumeGate
            items={items}
            capturedFiles={capturedFiles}
            serverCompleted={serverCompleted}
            completedItemsMedia={completedItemsMedia}
            assignment={assignment}
            workflow={workflow}
            branding={branding}
            onOpenNotes={handleOpenNotes}
            notes={notes}
            onAddNote={handleAddNote}
            onContinue={handleResumeContinue}
            token={cacheToken}
            syncStatus={syncStatus}
            onDismissSyncStatus={() => setSyncStatus(null)}
          />
        );
      case 'saved-exit':
        return (
          <SavedExit
            items={items}
            capturedFiles={capturedFiles}
            serverCompleted={serverCompleted}
            completedItemsMedia={completedItemsMedia}
            assignment={assignment}
            workflow={workflow}
            branding={branding}
            editableItemIds={sessionCapturedItemIds}
            onRetake={handleRetake}
            onDelete={handleDelete}
            onDeletePhoto={handleDeletePhoto}
            onAddPhoto={handleAddPhoto}
            onOpenNotes={handleOpenNotes}
            notes={notes}
            onAddNote={handleAddNote}
            onSave={handleConfirmSaveAndExit}
            onFinish={() => {
              clearStoredSavedExitPending(token);
              setPhase('saved-exit-done');
            }}
            token={cacheToken}
            pendingSyncCount={pendingSyncCount}
            syncStatus={syncStatus}
            onDismissSyncStatus={() => setSyncStatus(null)}
          />
        );
      case 'saved-exit-done':
        return (
          <Done
            assignment={assignment}
            items={items}
            tenant={tenant}
            branding={branding}
            workflow={workflow}
            completedItemsMedia={completedItemsMedia}
            notes={notes}
            content={{
              title: 'Your progress is saved',
              message: 'You can safely close this window. Reopen the link anytime to continue where you left off.',
              showWhatHappensNext: false,
            }}
          />
        );
      case 'otp-verification':
        return (
          <OtpVerification
            assignment={assignment}
            workflow={workflow}
            branding={branding}
            token={cacheToken}
            onVerified={() => {
              setCurrentStepIndex(0);
              setPhase('welcome');
            }}
          />
        );
      case 'welcome':
        return (
          <Welcome
            assignment={assignment}
            workflow={workflow}
            tenant={tenant}
            branding={branding}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            onViewInstructions={handleViewInstructions}
            token={cacheToken}
          />
        );
      case 'instructions':
        return (
          <Instructions
            assignment={assignment}
            items={items}
            branding={branding}
            workflow={workflow}
            serverCompleted={serverCompleted}
            onStart={handleStart}
            onBack={() => setPhase('welcome')}
            token={cacheToken}
            onOpenNotes={handleOpenNotes}
          />
        );
      case 'capturing':
        return (
          <Capturing
            assignment={assignment}
            workflow={workflow}
            tenant={tenant}
            currentItem={items[currentStepIndex]}
            currentStepIndex={currentStepIndex}
            items={items}
            capturedFiles={capturedFiles}
            serverCompleted={serverCompleted}
            completedItemsMedia={completedItemsMedia}
            sessionRecovered={sessionRecovered}
            branding={branding}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            accentBgClass={accentBgClass}
            brandSolidStyle={brandSolidStyle}
            onCapture={handleCapture}
            onSkip={handleSkip}
            onReview={() => setPhase('review')}
            onPrevious={handlePreviousStep}
            onRetake={handleRetake}
            onDeletePhoto={handleDeletePhoto}
            onAddPhoto={handleAddPhoto}
            token={token}
            retakeItemId={retakeItemId}
            location={location}
            replaceUploadContext={replaceUploadContext}
            multiPhotoReviewItemId={multiPhotoReviewItemId}
            onMultiPhotoAddMore={handleMultiPhotoAddMore}
            onMultiPhotoRetake={handleMultiPhotoRetake}
            onMultiPhotoNext={handleMultiPhotoNext}
            onMultiPhotoBack={handleMultiPhotoBack}
            offlineSupportEnabled={offlineSupportEnabled}
            onOpenNotes={NOTES_SAVE_EXIT_ENABLED ? () => openNotes(items[currentStepIndex]?.assignmentItemId) : undefined}
            onSaveAndExit={(!NOTES_SAVE_EXIT_ENABLED || assignment?.restrictToSingleSession) ? undefined : handleSaveAndExit}
          />
        );
      case 'review':
        return (
          <Review
            items={items}
            capturedFiles={capturedFiles}
            serverCompleted={serverCompleted}
            completedItemsMedia={completedItemsMedia}
            branding={branding}
            workflow={workflow}
            submitError={submitError}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            assignment={assignment}
            token={cacheToken}
            onOpenNotes={handleOpenNotes}
            notes={notes}
            onAddNote={handleAddNote}
            onRetake={handleRetake}
            onSubmit={handleSubmit}
            onStart={handleStart}
            onDelete={handleDelete}
            onDeletePhoto={handleDeletePhoto}
            onAddPhoto={handleAddPhoto}
            syncBlockedNotice={syncBlockedNotice}
            onDismissSyncBlockedNotice={() => setSyncBlockedNotice(false)}
            syncStatus={syncStatus}
            onDismissSyncStatus={() => setSyncStatus(null)}
          />
        );
      case 'submitting':
        return (
          <Submitting
            submitProgress={submitProgress}
            branding={branding}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
          />
        );
      case 'done':
        return (
          <Done
            assignment={assignment}
            tenant={tenant}
            branding={branding}
            workflow={workflow}
            items={items}
            completedItemsMedia={completedItemsMedia}
            completedItemsCount={captureData.completedItemsCount}
            token={cacheToken}
            notes={notes}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <AssetSyncOverlay isOpen={Boolean(captureData) && offlineSupportEnabled && isSyncingAssets} />

      {/* Only block on data-sync once the initial asset prefetch has settled —
          the two overlays would otherwise both cover the exact same "just
          reconnected on first load" window with redundant messaging. */}
      <DataSyncOverlay isOpen={Boolean(captureData) && offlineSupportEnabled && !isSyncingAssets && isSyncingData} />

      {captureData && offlineSupportEnabled && !['welcome', 'done', 'otp-verification'].includes(phase) && (
        <OfflineBanner isOnline={isOnline} pendingSyncCount={pendingSyncCount} />
      )}

      {/* 'review'/'resume-gate'/'saved-exit' render this same toast inline
          (above their own sticky action button, see Review.jsx/ReviewShell.jsx)
          instead of this viewport-fixed one, so it never floats over the
          button on those screens — skip it here to avoid showing it twice. */}
      {captureData && offlineSupportEnabled && !['welcome', 'done', 'otp-verification', 'review', 'resume-gate', 'saved-exit'].includes(phase) && (
        <SyncStatusToast
          status={syncStatus?.status}
          message={syncStatus?.message}
          onDismiss={() => setSyncStatus(null)}
        />
      )}

      {/* Only render the capture phases once captureData is available */}
      {captureData && renderPhase()}

      <NoteIdentityGate
        isOpen={pendingPhase !== null}
        token={token}
        branding={branding}
        isOnline={isOnline}
        onSubmit={() => {
          setPhase(pendingPhase);
          setPendingPhase(null);
        }}
      />

      {NOTES_SAVE_EXIT_ENABLED && (
        <NotesModal
          isOpen={isNotesModalOpen}
          onClose={() => {
            setIsNotesModalOpen(false);
            fetchNotesSummary();
          }}
          token={token}
          theme={branding.theme}
          assignmentItemId={notesStepItemId}
          offlineSupportEnabled={offlineSupportEnabled}
          startInAddMode={notesOpenMode === 'add'}
        />
      )}

      <Modal
        isOpen={showSessionConflictModal}
        onClose={handleCancelSessionConflict}
        dismissable={!NOTES_SAVE_EXIT_ENABLED}
        title="Active Session Detected"
      >
        {/* Icon */}
        <div className="w-[52px] h-[52px] rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4 text-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Body text */}
        <p className="text-sm text-base-content/70 text-center leading-relaxed mb-6">
          {sessionConflictMessage || (
            <>
              You already have an active session open on another device or browser.
              <strong className="text-base-content"> Please continue on your previous device.</strong>
            </>
          )}
        </p>

        {/* Actions */}
        {/* <div className="flex flex-col gap-3">
          <button
            id="session-conflict-start-new"
            onClick={handleStartNewSession}
            className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-primary-content bg-primary transition-opacity hover:opacity-90 active:scale-[0.98]"
          >
            Yes, Start New Session
          </button>

          <button
            id="session-conflict-cancel"
            onClick={handleCancelSessionConflict}
            className="w-full py-3 px-4 rounded-xl border border-base-content/20 text-sm font-medium text-base-content hover:bg-base-200 transition-colors"
          >
            No, Continue on Previous Device
          </button>
        </div> */}
      </Modal>

      <Modal
        isOpen={Boolean(expiryReminder) && !EXPIRY_REMINDER_EXCLUDED_PHASES.has(phase)}
        onClose={dismissExpiryReminder}
        title="Link expiring soon"
      >
        <p className="text-sm text-base-content/70 text-center leading-relaxed mb-6">
          {`You're offline and this link expires in ~${expiryReminder?.minutesRemaining} minute${expiryReminder?.minutesRemaining === 1 ? '' : 's'}. Reconnect soon so we can save your progress.`}
        </p>
        <button
          type="button"
          onClick={dismissExpiryReminder}
          className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-primary-content bg-primary transition-opacity hover:opacity-90 active:scale-[0.98]"
        >
          Got it
        </button>
      </Modal>

      <Modal
        isOpen={showExpiredOfflineAlert}
        onClose={() => {}}
        dismissable={false}
        title="Link Expired"
      >
        <p className="text-sm text-base-content/70 text-center leading-relaxed">
          Sync your data to prevent the loss of capture data.
        </p>
      </Modal>
    </>
  );
};

export default Capture;
